import React, { useState, useEffect } from "react";
import {
  AppBar, Toolbar, Typography, Box, Container,
  IconButton, Button, useTheme, useMediaQuery, Avatar,
  Drawer, List, ListItem, ListItemButton, ListItemText,
} from "@mui/material";
import {
  Search, NotificationsNoneOutlined, AdminPanelSettings,
  PlayCircleOutline,
  MenuOutlined, CloseOutlined,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { MEMBERSHIP_COLORS } from "./MembershipStyles";
import { useSearch } from "./SearchContext";
import { keyframes } from "@mui/material";
import LanguageSwitcher from "../common/LanguageSwitcher";
import { useLanguage } from "../../contexts/LanguageContext";

const BLUE      = MEMBERSHIP_COLORS.blue;
const NAVY      = MEMBERSHIP_COLORS.navy;
const WHITE     = MEMBERSHIP_COLORS.white;
const MUTED     = MEMBERSHIP_COLORS.muted;
const BORDER    = MEMBERSHIP_COLORS.border;

const slideDown = keyframes`
  from { opacity:0; transform:translateY(-10px); }
  to   { opacity:1; transform:translateY(0); }
`;

const MembershipNavbar: React.FC = () => {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const theme     = useTheme();
  const { openSearch } = useSearch();
  const { t } = useLanguage();
  const isMobile  = useMediaQuery(theme.breakpoints.down("md"));

  const navLinks = [
    { label: t.nav.coaching,  icon: <PlayCircleOutline  sx={{ fontSize:"1.1rem" }}/>, path: "/membership/coaching"   },
    { label: t.nav.classes,   icon: <PlayCircleOutline  sx={{ fontSize:"1.1rem" }}/>, path: "/membership/classes"    },
  ];

  // Derive the membership badge from the user's REAL subscription instead of
  // hard-coding "Pro Member" for everyone (which contradicted locked courses).
  const sub = user?.subscription;
  const subActive = sub?.status === "active";
  const planLabel = !subActive
    ? "Free member"
    : sub?.plan === "pro"
      ? "Elite member"
      : sub?.plan === "premium"
        ? "Pro member"
        : sub?.plan === "basic"
          ? "Starter member"
          : "Member";
  const [scrolled,    setScrolled]    = useState(false);
  const [drawerOpen,  setDrawerOpen]  = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const handleSearchClick = () => {
    if (!location.pathname.includes("/membership/classes")) navigate("/membership/classes");
    setTimeout(() => openSearch(), 100);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: scrolled ? "rgba(255,255,255,0.92)" : WHITE,
          backdropFilter: scrolled ? "blur(16px)" : "none",
          borderBottom: `1px solid ${scrolled ? "rgba(21,101,192,0.1)" : BORDER}`,
          boxShadow: scrolled ? "0 2px 20px rgba(21,101,192,0.08)" : "none",
          transition: "all 0.35s cubic-bezier(.4,0,.2,1)",
          zIndex: theme.zIndex.drawer + 1,
          animation: `${slideDown} .5s ease both`,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{
            justifyContent: "space-between",
            height: scrolled ? 68 : 80,
            transition: "height 0.35s ease",
            gap: 2,
          }}>

            {/* Logo */}
            <Box onClick={() => navigate("/")} sx={{ display:"flex", alignItems:"center", cursor:"pointer" }}>
              <Box component="img" src="/logo.png" alt="Dr.Paw" sx={{ height:{ xs:40, md:48 }, width:"auto", objectFit:"contain" }} />
            </Box>

            {/* Desktop nav links */}
            {!isMobile && (
              <Box sx={{ display:"flex", gap:1, alignItems:"center" }}>
                {navLinks.map((link) => {
                  const isActive = location.pathname === link.path ||
                    (link.path !== "/membership" && location.pathname.startsWith(link.path));
                  return (
                    <Button
                      key={link.label}
                      onClick={() => navigate(link.path)}
                      sx={{
                        px:2, py:1,
                        color: isActive ? BLUE : MUTED,
                        fontSize:".88rem",
                        fontWeight: isActive ? 700 : 500,
                        textTransform:"none",
                        borderRadius:"12px",
                        backgroundColor: isActive ? `rgba(21,101,192,.08)` : "transparent",
                        transition:"all .2s ease",
                        "&:hover":{ backgroundColor:"rgba(21,101,192,.06)", color:BLUE },
                        position:"relative",
                        "&::after": isActive ? {
                          content:'""', position:"absolute",
                          bottom:4, left:"50%", transform:"translateX(-50%)",
                          width:5, height:5, borderRadius:"50%",
                          backgroundColor:BLUE,
                        } : {},
                      }}
                    >
                      <Box sx={{ display:"flex", alignItems:"center", gap:.6 }}>
                        {link.icon}
                        {link.label}
                      </Box>
                    </Button>
                  );
                })}
              </Box>
            )}

            {/* Right actions */}
            <Box sx={{ display:"flex", alignItems:"center", gap:{ xs:1, md:1.8 } }}>
              {!isMobile && (
                <IconButton
                  sx={{
                    color:MUTED, border:`1px solid ${BORDER}`, borderRadius:"10px", p:.9,
                    "&:hover":{ color:BLUE, borderColor:`rgba(21,101,192,.3)`, backgroundColor:"rgba(21,101,192,.05)" },
                    transition:"all .2s ease",
                  }}
                >
                  <NotificationsNoneOutlined fontSize="small"/>
                </IconButton>
              )}

              {user?.type === "admin" && (
                <Button
                  onClick={() => navigate("/admin")}
                  startIcon={<AdminPanelSettings sx={{ fontSize:"1rem" }}/>}
                  size="small"
                  sx={{
                    textTransform:"none", fontWeight:700, fontSize:".8rem",
                    color:WHITE, backgroundColor:BLUE,
                    borderRadius:"10px", px:1.8, py:.8,
                    boxShadow:`0 4px 14px rgba(21,101,192,.3)`,
                    "&:hover":{ backgroundColor:NAVY, boxShadow:`0 6px 20px rgba(21,101,192,.4)` },
                    transition:"all .25s ease",
                    display:{ xs:"none", sm:"flex" },
                  }}
                >
                  Admin
                </Button>
              )}

              <LanguageSwitcher variant="dark" />

              <IconButton
                onClick={handleSearchClick}
                sx={{
                  color:MUTED, border:`1px solid ${BORDER}`, borderRadius:"10px", p:.9,
                  "&:hover":{ color:BLUE, borderColor:`rgba(21,101,192,.3)`, backgroundColor:"rgba(21,101,192,.05)" },
                  transition:"all .2s ease",
                }}
              >
                <Search fontSize="small"/>
              </IconButton>

              <Box
                onClick={() => navigate("/profile")}
                sx={{
                  display:"flex", alignItems:"center", gap:1,
                  cursor:"pointer", pl:.5,
                  transition:"opacity .2s ease", "&:hover":{ opacity:.8 },
                }}
              >
                <Avatar sx={{
                  width:36, height:36,
                  background:`linear-gradient(135deg,${BLUE},#42A5F5)`,
                  fontSize:".85rem", fontWeight:800,
                  boxShadow:`0 4px 12px rgba(21,101,192,.3)`,
                }}>
                  {user?.name?.charAt(0) || "U"}
                </Avatar>
                {!isMobile && (
                  <Box>
                    <Typography sx={{ fontWeight:700, fontSize:".85rem", color:NAVY, lineHeight:1.2 }}>
                      {user?.name || "Member"}
                    </Typography>
                    <Typography sx={{ fontSize:".68rem", color:MUTED }}>{planLabel}</Typography>
                  </Box>
                )}
              </Box>

              {/* Mobile hamburger */}
              {isMobile && (
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  sx={{ color:NAVY, border:`1px solid ${BORDER}`, borderRadius:"10px" }}
                >
                  <MenuOutlined/>
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width:{ xs:"100%", sm:320 }, backgroundColor:WHITE } }}
        transitionDuration={300}
      >
        <Box sx={{ p:3, display:"flex", justifyContent:"space-between", alignItems:"center",
          borderBottom:`1px solid ${BORDER}` }}>
          <Box sx={{ display:"flex", alignItems:"center" }}>
            <Box component="img" src="/logo.png" alt="Dr.Paw" sx={{ height:40, width:"auto", objectFit:"contain" }} />
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ border:`1px solid ${BORDER}`, borderRadius:"10px" }}>
            <CloseOutlined sx={{ color:NAVY }}/>
          </IconButton>
        </Box>
        <List sx={{ px:2, py:3 }}>
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <ListItem key={link.label} disablePadding sx={{ mb:1 }}>
                <ListItemButton
                  onClick={() => { navigate(link.path); setDrawerOpen(false); }}
                  sx={{
                    borderRadius:"12px", py:1.5,
                    backgroundColor: isActive ? "rgba(21,101,192,.08)" : "transparent",
                    "&:hover":{ backgroundColor:"rgba(21,101,192,.06)" },
                  }}
                >
                  <Box sx={{ mr:1.5, color: isActive ? BLUE : MUTED }}>{link.icon}</Box>
                  <ListItemText
                    primary={link.label}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? BLUE : NAVY,
                      fontSize:".95rem",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>
    </>
  );
};

export default MembershipNavbar;
